import { OBSTACLE_MIN_ZOOM } from "./webgl-maplibre.obstacle-symbols";
import { SettingsService } from "../settings/settings.service";
import { selectedBaseCenter } from "../../pages/base-briefing/base-map-focus";
import "./webgl-maplibre.worker-url";

import { uniqueAirspaceGeometry } from "./webgl-maplibre.airspace-geometry";
import { setAirportBuildingHeights } from "./webgl-maplibre.buildings";
import { buildAirspaceLabels } from "./webgl-maplibre.airspace-labels";
import { AirspaceStyleFile, loadAirspaceStyle, applyAirspaceStyle } from "./webgl-maplibre.airspace-style";
import { Injectable, NgZone, OnDestroy, inject } from "@angular/core";
import { PLATFORM_ADAPTER } from "../../core/platform/platform.adapter";
import { GeoJSONSource, LngLat, LngLatBounds, Map as MlMap } from "maplibre-gl";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import { getElevationOfGeoPos } from "src/app/plugins/opsctrl-foc/class/navLogReport";
import { classCom } from "src/app/plugins/opsctrl-foc/focMain";
import { pmtilesElevAtSync } from "../terrain/terrain-pmtiles";
import { showAeroPlane, hideAeroPlane } from "./webgl-maplibre.aero-plane";
import { ProcedurePlanes } from "./webgl-maplibre.procedure-planes";
import { VectorPlane } from "./webgl-maplibre.vector-plane";
import { RoutePlane } from "./webgl-maplibre.route-plane";
import { RouteTour, routeTerrainSamples } from "./webgl-maplibre.route-tour";
import { tourPerformance } from "./webgl-maplibre.tour-performance";
import { containsView, expandedView, ViewBounds } from "./webgl-maplibre.viewport-cache";
import { hideVectorLabelsDuringRotation } from "./webgl-maplibre.rotation-labels";
import { ApproachSectors } from "./webgl-maplibre.approach-sectors";
import { environment } from "../../../environments/environment";
import { mapWithConcurrency } from "../terrain/route-elevation-profile";
import { AuthService } from "../auth/auth.service";
import { ConfigService } from "../config/config.service";
import { DatabaseService } from "../database/database.service";
import { MAP_PROCESSES } from "../database/process-db-names";
import { MapLayerAsGeoJsonGQL, MapLayerUpdatesGQL } from "../efb-api/efb-api.service";
import { EventsService } from "../events/events.service";
import { GeolocationService } from "../geolocation/geolocation.service";
import { MapObjectsService } from "../map-objects/map-objects.service";
import { PointInterface } from "../route/route.service";
import { StorageService } from "../storage.service";
import { TrackingService } from "../tracking/tracking.service";
import { emptyFC, lineFC, safeNum } from "./webgl-maplibre.helpers";
import { resetDemSourceState } from "./webgl-maplibre.dem-source";
import { hasHillshadePacks, hideHillshade, resetHillshadeState, showHillshade } from "./webgl-maplibre.hillshade";
import { ReliefOverlay, ceilingColor, hasReliefPacks, terrainColor } from "./webgl-maplibre.relief-overlay";
import { Obstacle3dLayer, lngLatElevToMercator } from "./webgl-maplibre.obstacle-3d-layer";
import { isLightedObstacleSrc } from "./webgl-maplibre.obstacle-symbols";
import {
  TERRAIN_EXAGGERATION,
  has3dTerrainPacks,
  hide3dTerrain,
  resetTerrain3dState,
  show3dTerrain,
} from "./webgl-maplibre.terrain3d";
import { pmtilesElevAt } from "../terrain/terrain-pmtiles";
import { IWebglMapService } from "./webgl-maplibre.interface";
import {
  addOverlaySourcesAndLayers,
  addRasterLayers,
  chartTransitionZoom,
  addVectorSourceAndLayers,
  attributionsInView,
  collectAttributionLayers,
  AttributionLayer,
  setGeoJsonData,
} from "./webgl-maplibre.layers";
import {
  CITY_SOURCE_ID,
  CityRow,
  OrthoCoverage,
  addCityLabelLayer,
  cityLabelFeatures,
  CITY_QUERY_LIMIT,
  CityBox,
  boxContains,
  paddedCityBox,
  orthoCoverage,
} from "./webgl-maplibre.city-labels";
import { buildAircraftNavFC, computeCursorInfo, deduplicateCoords } from "./webgl-maplibre.navigation";
import { startTileServer } from "./webgl-maplibre.tile-protocol";
import { TrafficManager } from "./webgl-maplibre.traffic";
import { ControlFileData, MapCenterEvent, MapConfig, MapSettings } from "./webgl-maplibre.types";

const MOVE_END_DEBOUNCE_MS = 240;
const MIN_ZOOM = 6;
// Deepest zoom with real imagery: Bayern DOP is native ~0.20 m/px at z19, the
// Vienna ortho ~0.30 m/px. Past this MapLibre only upscales, so there is no
// detail to gain. Drives BOTH the map's interactive ceiling and the clamp in
// changeZoom(), so the buttons can always reach exactly as deep as a pinch.
// (Was Math.log2(156543.03 / 0.4) - 1 = 17.58, derived from a 0.4 m/px
// assumption that predates the 0.20 m/px Bayern source.)
const MAX_ZOOM = 19;

// Parse a CSS colour string ("rgba(r,g,b,a)", "rgb(r,g,b)", "#rgb", "#rrggbb",
// "#rrggbbaa") to normalized RGBA floats, for the 3D cable colour.
// Falls back to translucent amber on anything it can't parse.
const CONE_FALLBACK_RGBA: [number, number, number, number] = [1.0, 0.8, 0.1, 0.35];
function cssColorToRgba(s: unknown): [number, number, number, number] {
  if (typeof s !== "string") return CONE_FALLBACK_RGBA;
  const str = s.trim();
  const m = str.match(/^rgba?\(([^)]+)\)$/i);
  if (m) {
    const p = m[1].split(",").map((x) => parseFloat(x.trim()));
    if (p.length >= 3 && p.every((n, i) => isFinite(n) || i >= 3)) {
      const a = p.length >= 4 && isFinite(p[3]) ? p[3] : 1;
      return [Math.min(1, p[0] / 255), Math.min(1, p[1] / 255), Math.min(1, p[2] / 255), Math.min(1, Math.max(0, a))];
    }
  }
  if (str[0] === "#") {
    let h = str.slice(1);
    if (h.length === 3)
      h = h
        .split("")
        .map((c) => c + c)
        .join("");
    if (h.length === 6 || h.length === 8) {
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      const a = h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
      if ([r, g, b].every((n) => isFinite(n))) return [r / 255, g / 255, b / 255, a];
    }
  }
  return CONE_FALLBACK_RGBA;
}

@Injectable({ providedIn: "root" })
export class WebglMapLibreService implements IWebglMapService, OnDestroy {
  public mapObj: any = undefined;
  public mapReady: boolean = false;
  public terrainCtrl: any = undefined;
  public destinationPos = { point: [] as number[], valid: false };
  public destMapLayerSetting = { name: "", id: 0, zoom: 10 };
  public cursor: any = undefined;
  public hideNavigRoute: boolean = false;

  private database = inject(DatabaseService);
  private settings = inject(SettingsService);
  private zone = inject(NgZone);
  private configService = inject(ConfigService);
  private airspaceStyle?: AirspaceStyleFile;
  private geolocation = inject(GeolocationService);
  private events = inject(EventsService);
  private storage = inject(StorageService);
  private mapObjCtrl = inject(MapObjectsService);
  private authService = inject(AuthService);
  private mapLayerAsGeoJsonGQL = inject(MapLayerAsGeoJsonGQL);
  private mapLayerUpdatesGQL = inject(MapLayerUpdatesGQL);
  private tracking = inject(TrackingService);
  private platformAdapter = inject(PLATFORM_ADAPTER);

  private map: MlMap | null = null;
  private attributionLayers: AttributionLayer[] = [];
  private mapContainer: HTMLDivElement | null = null;
  private cityCoverage: OrthoCoverage | null = null;
  private cityLabelSeq = 0;
  /** The box the label source currently holds, or null when it holds an
   *  unknown or truncated set. A view inside it needs no query. */
  private cityLoadedBox: CityBox | null = null;
  private vectorRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private vectorRefreshInFlight = false;
  private vectorCoverage?: { map: MlMap; bounds: ViewBounds; resolution: number; at: number; features: any[] };
  private obstacleCoverage?: { map: MlMap; bounds: ViewBounds; at: number };
  private obstacleRefreshBusy = false;
  private obstacleRefreshQueued = false;
  private vectorRefreshQueued = false;
  private moveEndTimeout: ReturnType<typeof setTimeout> | null = null;
  private mapReadyResolvers: Array<() => void> = [];
  private mapInitializing = false;

  private clickEventDisabled = false;
  private isCentered = true;
  private mapClickItemsTimeout: ReturnType<typeof setTimeout> | null = null;
  // A brand may set `environment.mapStart` (ARA opens on Klagenfurt); the others keep [13, 47].
  private readonly mapStart = (environment as { mapStart?: { center: number[]; zoom: number } }).mapStart;
  private mapSettings: MapSettings = {
    center: this.mapStart?.center ?? [13, 47],
    zoom: this.mapStart?.zoom ?? 10,
    isCentered: true,
  };
  private geolocationSubscription: Subscription | null = null;
  private signalStatusSubscription: Subscription | null = null;
  private activeRouteCoords: number[][] | null = null;
  private plannedRouteCoords: number[][] = [];
  private trafficManager: TrafficManager | null = null;
  // Latest traffic while pauseGeolocation() holds the map: each update redraws the hidden canvas,
  // and those frames grow the WebKit GPU process. startGeolocationSubscription() applies it.
  private pendingTraffic: GeoJSON.FeatureCollection | null = null;
  private aircraftImageLoaded = false;
  private longpressTimer: ReturnType<typeof setTimeout> | null = null;
  private longpressStartPos: { x: number; y: number } | null = null;
  private trackingState: number = 0; // 0=none, 1=current, 2=loaded
  private terrainUrl: string = "";
  private hillshadeEnabled: boolean = false;
  private terrain3dEnabled: boolean = false;
  private rebuild3dAfterRestore = false;
  private obstacle3dLayer: Obstacle3dLayer | null = null;
  private obstacle3dLayerId = "obstacle-3d";
  private obstacle3dRefreshSeq = 0;
  // Original vm-* filters captured the first time we suppress obstacles, so
  // we can restore them when 3D is disabled. Captured lazily from the map.
  private vmFilterOriginals: Record<string, unknown> | null = null;
  private cursorMoveSeq: number = 0;

  private _currentTrafficSubject$ = new BehaviorSubject<any>({});
  // BehaviorSubject so a fresh MapPageComponent (root-scoped service, page
  // re-entry) replays the current screen-space position instead of waiting
  // for the next move/GPS event.
  private _aircraftScreenPos$ = new BehaviorSubject<{ x: number; y: number } | null>(null);
  public readonly aircraftScreenPos$ = this._aircraftScreenPos$.pipe(
    distinctUntilChanged((a, b) => a === b || (!!a && !!b && a.x === b.x && a.y === b.y))
  );

  get currentTrafficSubject$(): Observable<any> {
    return this._currentTrafficSubject$.asObservable();
  }

  async init(): Promise<void> {
    // no-op — initialization happens in setTarget
  }

  constructor() {
    // Traffic subscription pushes via Apollo GraphQL WS against the device
    // efb-api endpoint (wss://<api>/graphql). The SPA has no such endpoint
    // and the LAN IP fails SSL handshake — gate to native.
    if (this.platformAdapter.isNative) {
      this.receiveTraffic();
    }
  }

  // --------------- helpers ---------------

  private setSourceData(sourceId: string, fc: GeoJSON.FeatureCollection): void {
    // `this.map.style` becomes null during setStyle()/remove(); mapReady alone doesn't catch that window.
    if (!this.map?.style || !this.mapReady) return;
    if (sourceId === "vectormap") {
      setAirportBuildingHeights(fc.features);
      this.fullVectorData = fc;
      if (this.approachSectors) {
        void this.approachSectors.setData(fc);
        return;
      }
    }
    if (this.routePlane?.setData(sourceId, fc)) return;
    if (this.vectorPlane?.setData(sourceId, fc)) return;
    setGeoJsonData(this.map, sourceId, fc);
  }

  // --------------- map initialization ---------------

  async setTarget(target: HTMLDivElement, height: number, width: number): Promise<void> {
    this.mapInitializing = true;
    const tileBaseUrl = await startTileServer();

    // If map already exists, just re-attach its container to the new target
    if (this.map && this.mapContainer) {
      while (target.firstChild) {
        target.removeChild(target.firstChild);
      }
      target.appendChild(this.mapContainer);
      this.map.resize();
      this.mapReady = true;
      for (const resolve of this.mapReadyResolvers) {
        resolve();
      }
      this.mapReadyResolvers = [];
      // pauseGeolocation() stopped these when the page was left.
      if (!this.geolocationSubscription) this.startGeolocationSubscription();
      // Replay current state to a freshly entered MapPageComponent. Without
      // this, the page renders as centered (its hardcoded default) until the
      // next move/GPS event arrives, even if the user left off-center.
      this.republishCurrentMapState();
      return;
    }

    // Clear target children
    while (target.firstChild) {
      target.removeChild(target.firstChild);
    }

    // Create a wrapper div for the map
    this.mapContainer = document.createElement("div");
    this.mapContainer.style.width = "100%";
    this.mapContainer.style.height = "100%";
    this.mapContainer.style.position = "relative";
    target.appendChild(this.mapContainer);

    const config = await this.configService.configP;
    this.airspaceStyle = await loadAirspaceStyle(config.map.styleUrl);
    this.terrainUrl = config.map.terrain.url;
    this.destMapLayerSetting.zoom = config.map.dest.zoom;
    const extent = [
      [7, 44],
      [20, 51.3],
    ]; // TODO: restore config.map.extent after testing

    // Load saved map settings from storage
    const savedSettings = await this.storage.get("mapSettings");
    if (savedSettings) {
      if (Array.isArray(savedSettings.center) && savedSettings.center.length === 2) {
        this.mapSettings.center = savedSettings.center;
      }
      if (typeof savedSettings.zoom === "number" && isFinite(savedSettings.zoom)) {
        this.mapSettings.zoom = savedSettings.zoom;
      }
      if (typeof savedSettings.isCentered === "boolean") {
        this.mapSettings.isCentered = savedSettings.isCentered;
        this.isCentered = savedSettings.isCentered;
      }
    }

    await this.settings.loaded;
    const baseCenter = selectedBaseCenter(await this.settings.getSetting("base"), config.bases);
    if (baseCenter) {
      this.mapSettings.center = baseCenter;
      this.mapSettings.zoom = config.map.dest.zoom;
      this.mapSettings.isCentered = false;
      this.isCentered = false;
    } else if (config.map.startupView) {
      this.mapSettings.center = [...config.map.startupView.center];
      this.mapSettings.zoom = config.map.startupView.zoom;
      this.mapSettings.isCentered = false;
      this.isCentered = false;
    }

    // `?at=lat,lng,zoom[,bearing]` in the page URL opens the map at that view.
    // A testing aid, for example `/map?at=46.92,13.86,14,90`.
    const at = new URLSearchParams(window.location.search).get("at")?.split(",").map(Number) ?? [];
    let bearing = 0;
    if (at.length >= 3 && at.every(Number.isFinite)) {
      this.mapSettings.center = [at[1], at[0]];
      this.mapSettings.zoom = at[2];
      bearing = at[3] ?? 0;
      this.mapSettings.isCentered = false;
      this.isCentered = false;
    }

    // Outside Angular's zone: otherwise every MapLibre frame, mouse move and
    // wheel event runs change detection (about 3 s of a 35 s camera profile).
    // The handlers that change page state re-enter the zone themselves.
    this.map = this.zone.runOutsideAngular(
      () =>
        new MlMap({
          container: this.mapContainer,
          style: {
            version: 8,
            sources: {},
            layers: [
              {
                id: "background",
                type: "background",
                paint: { "background-color": "rgb(50,50,50)" },
              },
            ],
            glyphs: "assets/fonts/{fontstack}/{range}.pbf",
          },
          center: [this.mapSettings.center[0], this.mapSettings.center[1]],
          zoom: this.mapSettings.zoom,
          bearing,
          minZoom: MIN_ZOOM,
          maxZoom: MAX_ZOOM,
          maxPitch: environment.enableThreeD ? 60 : 0,
          // Stays off: MapLibre's AttributionControl sets pointer-events:auto on
          // itself inside the canvas container, so it swallows drags/longpresses in
          // the bottom-right corner (cursor placement + airspace lookup), and its
          // bottom-right slot is covered by .cursorInfo / .bottomList anyway. The
          // CC-BY credit for orthoBy is rendered as a static, pointer-events:none
          // label in map-page.component.html instead.
          attributionControl: false,
        })
    );
    // MapLibre requests every frame here. A frame requested from Angular code
    // would run in the zone, and so would each frame it chains.
    const triggerRepaint = this.map.triggerRepaint.bind(this.map);
    this.map.triggerRepaint = () => this.zone.runOutsideAngular(triggerRepaint);

    this.mapObj = this.map;

    const mapConfig = config.map as MapConfig;

    this.map.on("load", () =>
      this.zone.run(() => {
        if (!this.map) return;

        addRasterLayers(this.map, mapConfig, tileBaseUrl);
        // Straight after the rasters: the labels must sit above the imagery and
        // below every other layer, so they never cover the route or an obstacle.
        this.cityCoverage = orthoCoverage(mapConfig);
        this.cityLoadedBox = null;
        if (this.cityCoverage) addCityLabelLayer(this.map, this.cityCoverage.minZoom);
        this.attributionLayers = collectAttributionLayers(mapConfig);
        this.publishVisibleAttributions();
        addVectorSourceAndLayers(this.map, chartTransitionZoom(mapConfig));
        hideVectorLabelsDuringRotation(this.map);
        addOverlaySourcesAndLayers(this.map)
          .then((loaded) => {
            this.aircraftImageLoaded = loaded;
          })
          .catch((err) => {
            console.error("[WebglMapLibreService] Failed to set up overlay layers", err);
          });
        this.setupMapEventHandlers();
        this.startGeolocationSubscription();
        this.mapReady = true;
        this.mapInitializing = false;

        // Resolve any pending mapIsReady waiters
        for (const resolve of this.mapReadyResolvers) {
          resolve();
        }
        this.mapReadyResolvers = [];

        // Initial refresh
        this.refreshVectorSource();
        void this.refreshCityLabels();
      })
    );
  }

  // --------------- map event handlers ---------------

  private setupMapEventHandlers(): void {
    if (!this.map) return;

    // After a WebGL context loss, MapLibre restores the style but not custom
    // layers: the obstacles, the 3D route and the procedures would stay gone.
    // The app calls no setStyle, so style.load after a loss is the restore.
    this.map.on("webglcontextlost", () => {
      this.rebuild3dAfterRestore ||= this.terrain3dEnabled;
    });
    this.map.on("style.load", () => {
      const rebuild = this.rebuild3dAfterRestore && this.terrain3dEnabled;
      this.rebuild3dAfterRestore = false;
      if (rebuild) void this.setTerrain3dEnabled(false).then(() => this.setTerrain3dEnabled(true));
    });

    this.map.on("dragstart", () =>
      this.zone.run(() => {
        this.explicitMapSelection = false;
        this.mapSettings.isCentered = false;
        this.isCentered = false;
        // Flip the page's isCentered flag before moveend so the SVG cross + line
        // appear at the start of the drag, not at its end.
        this.publishIsCenteredFalse();
        this.emitAircraftScreenPos();
        if (this.mapClickItemsTimeout) {
          clearTimeout(this.mapClickItemsTimeout);
          this.mapClickItemsTimeout = null;
        }
      })
    );

    this.map.on("zoomstart", (event) => {
      if (event.originalEvent) this.explicitMapSelection = false;
      this.labelCamera = null;
      this.setSourceData("airspace-labels", emptyFC());
    });

    this.map.on("movestart", () => {
      this.scheduleVectorRefresh();
      if (this.moveEndTimeout) {
        clearTimeout(this.moveEndTimeout);
        this.moveEndTimeout = null;
      }
    });

    this.map.on("move", () => {
      this.scheduleVectorRefresh();
      if (this.routeTour?.isPlaying || this.mapSettings.isCentered || !this.map) return;
      const c = this.map.getCenter();
      // The cursor line and the ELEV/DIST bar follow the camera, so this one
      // still runs change detection once per moved frame.
      this.zone.run(() => {
        this.emitAircraftScreenPos();
        this.moveCursorTo([c.lng, c.lat], true);
      });
    });

    this.map.on("moveend", () =>
      this.zone.run(() => {
        // Tour frames are immediate camera updates; use the throttled move refresh.
        if (this.routeTour?.isPlaying) return;
        // Credits are viewport-gated (e.g. hide the Bayern credit over Austria),
        // so refresh them whenever the view settles.
        this.publishVisibleAttributions();
        // Query immediately at the final camera position; debounce only expensive 3D work.
        if (this.vectorRefreshTimer) {
          clearTimeout(this.vectorRefreshTimer);
          this.vectorRefreshTimer = null;
        }
        void this.refreshVectorSource();
        void this.refreshCityLabels();
        // Debounce obstacle refresh
        this.moveEndTimeout = setTimeout(() => {
          if (this.terrain3dEnabled) this.refreshObstacles3d();
        }, MOVE_END_DEBOUNCE_MS);

        if (!this.mapSettings.isCentered && this.map && !this.explicitMapSelection) {
          const c = this.map.getCenter();
          this.moveCursorTo([c.lng, c.lat], true);

          if (this.mapClickItemsTimeout) {
            clearTimeout(this.mapClickItemsTimeout);
          }
          this.mapClickItemsTimeout = setTimeout(() => {
            const zoom = this.map ? this.map.getZoom() : 10;
            this.mapObjCtrl
              .getMapObjects(this.mapSettings.center, zoom, [
                this.mapObjCtrl.webcam,
                this.mapObjCtrl.weather,
                this.mapObjCtrl.notam,
              ])
              .then((data) => {
                if (this.explicitMapSelection) return;
                this.events.publish(
                  "mapClickItems",
                  this.getZoom() > this.mapObjCtrl.cursorSelectionMinZoom ? data : []
                );
              })
              .catch((err) => console.error("getMapObjects: ", err));
          }, 500);
        }
      })
    );

    this.map.on("zoom", () => {
      if (!this.explicitMapSelection && this.getZoom() <= this.mapObjCtrl.cursorSelectionMinZoom) {
        this.zone.run(() => this.events.publish("mapClickItems", []));
      }
    });

    this.map.on("zoomend", () => {
      if (this.map) console.log("[map] zoom:", this.map.getZoom());
    });

    this.map.on("click", () => {
      if (!this.clickEventDisabled) {
        this.zone.run(() => this.events.publish("click-map", {}));
      }
    });

    this.zone.runOutsideAngular(() => this.addLongpressListeners());
  }

  private addLongpressListeners(): void {
    if (!this.map) return;
    // Longpress detection — right-click (desktop)
    const canvas = this.map.getCanvas();
    canvas.addEventListener("contextmenu", (e: MouseEvent) => {
      e.preventDefault();
      this.handleLongpress();
    });

    // Longpress detection — touch (mobile)
    canvas.addEventListener("touchstart", (e: TouchEvent) => {
      if (e.touches.length === 1) {
        this.longpressStartPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        this.longpressTimer = setTimeout(() => {
          this.handleLongpress();
        }, 500);
      }
    });

    canvas.addEventListener("touchmove", (e: TouchEvent) => {
      if (this.longpressStartPos && e.touches.length === 1) {
        const dx = e.touches[0].clientX - this.longpressStartPos.x;
        const dy = e.touches[0].clientY - this.longpressStartPos.y;
        if (Math.sqrt(dx * dx + dy * dy) > 10) {
          this.cancelLongpress();
        }
      }
    });

    canvas.addEventListener("touchend", () => {
      this.cancelLongpress();
    });

    canvas.addEventListener("touchcancel", () => {
      this.cancelLongpress();
    });
  }

  // --------------- refresh ---------------

  private scheduleVectorRefresh(): void {
    if (!this.map || this.vectorRefreshTimer) return;
    this.vectorRefreshTimer = setTimeout(
      () => {
        this.vectorRefreshTimer = null;
        this.procedurePlanes?.refreshViewport();
        void this.refreshVectorSource();
      },
      this.routeTour?.isPlaying ? 2000 : 500
    );
  }

  // The camera and data of the last airspace label build.
  private labelCamera: {
    map: MlMap;
    features: any[];
    zoom: number;
    bearing: number;
    pitch: number;
    center: LngLat;
  } | null = null;

  /**
   * Builds the airspace labels, except when the data is the same and the camera only moved by
   * less than 1/8 of the view. Following the GPS moves the camera every second, and each build
   * reloads the label source: new label bitmaps in the worker and a new glyph atlas texture,
   * which is where the field OOM (Texture._uploadRawData) happened. The labels keep their
   * geographic anchors between builds, so a small move needs no new placement.
   */
  private rebuildAirspaceLabels(map: MlMap, features: any[], force: boolean): void {
    const last = this.labelCamera;
    if (
      !force &&
      last?.map === map &&
      last.features === features &&
      last.zoom === map.getZoom() &&
      last.bearing === map.getBearing() &&
      last.pitch === map.getPitch()
    ) {
      const was = map.project(last.center),
        now = map.project(map.getCenter());
      const canvas = map.getCanvas();
      if (Math.hypot(was.x - now.x, was.y - now.y) < Math.min(canvas.clientWidth, canvas.clientHeight) / 8) return;
    }
    this.labelCamera = {
      map,
      features,
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
      center: map.getCenter(),
    };
    this.setSourceData("airspace-labels", buildAirspaceLabels(map, features));
  }

  private async refreshVectorSource(): Promise<void> {
    if (!this.map) return;

    if (this.vectorRefreshInFlight) {
      this.vectorRefreshQueued = true;
      return;
    }
    this.vectorRefreshInFlight = true;
    const refreshStarted = performance.now();
    this.vectorRefreshQueued = false;
    const requestedMap = this.map;
    const bounds = this.map.getBounds();
    const zoom = this.map.getZoom();
    // Convert zoom to resolution for DB query (DB stores scale in resolution units)
    const EQUATORIAL_RES_Z0 = 156543.03;
    const resolution = EQUATORIAL_RES_Z0 / Math.pow(2, zoom);
    const roundedRes = Math.round(resolution * 10) / 10;

    const view = { west: bounds.getWest(), south: bounds.getSouth(), east: bounds.getEast(), north: bounds.getNorth() };
    // The last query covers a margin around its view. While the camera stays inside it, skip
    // the query and the setData: setData reloads every visible tile of the source (new
    // buffers and atlas textures), and following the GPS moves the camera every second.
    // That churn grew WebKit's WebGL memory by ~+35 MiB/min in the memory rig (2026-09-24).
    const cached = this.vectorCoverage;
    if (
      cached?.map === requestedMap &&
      cached.resolution === roundedRes &&
      performance.now() - cached.at < 30000 &&
      containsView(cached.bounds, view)
    ) {
      this.vectorRefreshInFlight = false;
      if (this.routeTour?.isPlaying) {
        if (this.terrain3dEnabled) void this.refreshObstacles3d();
      } else if (!requestedMap.isMoving()) {
        this.rebuildAirspaceLabels(requestedMap, cached.features, false);
      }
      return;
    }

    const margin = this.routeTour?.isPlaying ? 0.6 : 0.2;
    const lonMargin = (bounds.getEast() - bounds.getWest()) * margin;
    const latMargin = (bounds.getNorth() - bounds.getSouth()) * margin;
    const minLon = safeNum(bounds.getWest() - lonMargin);
    const minLat = safeNum(Math.max(-85.051129, bounds.getSouth() - latMargin));
    const maxLon = safeNum(bounds.getEast() + lonMargin);
    const maxLat = safeNum(Math.min(85.051129, bounds.getNorth() + latMargin));
    const safeRes = safeNum(roundedRes);

    const entryName =
      resolution > 25 ? "vectorMapGeometriesInBoundsLowDetail" : "vectorMapGeometriesInBoundsHighDetail";

    try {
      const dataRaw = await this.database.namedQuery(entryName, {
        minX: minLon,
        maxX: maxLon,
        minY: minLat,
        maxY: maxLat,
        resolution: safeRes,
      });

      if (this.map !== requestedMap) return;

      const features: any[] = [];

      for (let i = 0; i < dataRaw.length; i++) {
        const f = JSON.parse(dataRaw[i].geoJson);
        f.id = dataRaw[i].id;
        // Stamp the geometry type from the row so vm-* filters can target it
        // (e.g. type=6 obstacle suppression while the 3D obstacle layer is on).
        // Suppression by feature id is unreliable because mapGeometries
        // queries fan out across MAP_PROCESSES with per-DB PK spaces — id=42
        // in ofm_navdata and id=42 in weather are different rows.
        f.properties._objType = dataRaw[i].type;
        applyAirspaceStyle(f.properties, this.airspaceStyle);
        if (f.geometry.type === "Point" && f.properties.text) {
          f.properties._isLabel = true;
        }
        features.push(f);
      }

      const fc: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: uniqueAirspaceGeometry(features) };
      this.setSourceData("vectormap", fc);
      this.vectorCoverage = {
        map: requestedMap,
        bounds: { west: minLon, south: minLat, east: maxLon, north: maxLat },
        resolution: roundedRes,
        at: performance.now(),
        features,
      };
      if (this.routeTour?.isPlaying && this.terrain3dEnabled) this.refreshObstacles3d();
      // Existing labels follow geographic anchors during panning; rebuild only at rest.
      if (requestedMap.isZooming()) {
        this.labelCamera = null;
        this.setSourceData("airspace-labels", emptyFC());
      } else if (!requestedMap.isMoving()) this.rebuildAirspaceLabels(requestedMap, features, true);
    } catch (e) {
      console.error("[WebglMapLibreService] refreshVectorSource error", e);
    } finally {
      this.vectorRefreshInFlight = false;
      tourPerformance.vector(performance.now() - refreshStarted);
      if (this.vectorRefreshQueued && this.map) {
        this.vectorRefreshQueued = false;
        void this.refreshVectorSource();
      }
    }
  }

  /** Reads the places in view for the ortho labels (#955).
   *
   *  A stale answer is dropped by sequence rather than queued: the labels are
   *  decoration for the current view, so the newest read is the only one worth
   *  drawing. Reading below the ortho's zoom would be wasted work, and the
   *  layer is hidden there anyway. */
  private async refreshCityLabels(): Promise<void> {
    if (!this.map || !this.cityCoverage) return;
    const coverage = this.cityCoverage;
    const requestedMap = this.map;
    const seq = ++this.cityLabelSeq;
    if (this.map.getZoom() < coverage.minZoom) {
      this.setSourceData(CITY_SOURCE_ID, emptyFC());
      this.cityLoadedBox = null;
      return;
    }
    const bounds = this.map.getBounds();
    const view: CityBox = {
      minX: bounds.getWest(),
      maxX: bounds.getEast(),
      minY: Math.max(-85.051129, bounds.getSouth()),
      maxY: Math.min(85.051129, bounds.getNorth()),
    };
    // The source already covers this view, so the labels are on screen the
    // moment the camera reaches them. Panning inside the pad costs no query.
    if (this.cityLoadedBox && boxContains(this.cityLoadedBox, view)) return;
    const box = paddedCityBox(view);
    try {
      const rows = await this.database.namedQuery("citiesInBounds", {
        minX: safeNum(box.minX),
        maxX: safeNum(box.maxX),
        minY: safeNum(box.minY),
        maxY: safeNum(box.maxY),
      });
      if (seq !== this.cityLabelSeq || this.map !== requestedMap) return;
      // A truncated result is the top 200 of the PADDED box, so it can omit a
      // small place inside the view itself. Do not cache it: the next settle
      // re-queries, and by then the view is usually smaller than the pad.
      const truncated = rows.length >= CITY_QUERY_LIMIT;
      this.cityLoadedBox = truncated || !boxContains(box, view) ? null : box;
      this.setSourceData(CITY_SOURCE_ID, cityLabelFeatures(rows as unknown as CityRow[], coverage));
    } catch (e) {
      // Every OEAMTC device is here: the cities pack is registered in dev-ara
      // only, nothing seeds the DB, and a read of a missing one fails. The map
      // must keep working, so the labels stay empty and nothing is reported.
      this.cityLoadedBox = null;
      console.warn("[WebglMapLibreService] no city labels: ", e);
    }
  }

  // --------------- navigation helpers ---------------

  /** Re-emit mapCenter + aircraftScreenPos so a freshly subscribed page
   *  catches up to the existing service state. */
  private republishCurrentMapState(): void {
    if (!this.map) return;
    if (this.mapSettings.isCentered) {
      const geo = this.geolocation.getGeoInfo();
      const centered: MapCenterEvent = {
        isCentered: true,
        center: [geo?.longitude ?? this.mapSettings.center[0], geo?.latitude ?? this.mapSettings.center[1]],
        time: 0,
        distanceRaw: 0,
        elevation: 0,
        heading: 0,
        info: null,
      };
      this.events.publish("mapCenter", centered);
      this._aircraftScreenPos$.next(null);
    } else {
      this.moveCursorTo(this.mapSettings.center, false);
      this.emitAircraftScreenPos();
    }
  }

  /** Cheap mapCenter publish that only flips the page's `isCentered` flag.
   *  Used on `dragstart` to render the SVG cross + line at the start of the
   *  drag without running computeCursorInfo or an elevation lookup — moveend
   *  will publish the full cursor info shortly after. */
  private publishIsCenteredFalse(): void {
    if (!this.map) return;
    const c = this.map.getCenter();
    const centered: MapCenterEvent = {
      isCentered: false,
      center: [c.lng, c.lat],
      time: 0,
      distanceRaw: 0,
      elevation: null,
      heading: 0,
      info: null,
    };
    this.events.publish("mapCenter", centered);
  }

  /** Publishes cursor info (with optional elevation lookup). The screen-space
   *  SVG crosshair + connecting line are driven by the page off `mapCenter`
   *  (visibility) and `aircraftScreenPos$` (geometry).
   *
   *  When `forceElevation` is set and a PMTiles DEM pack covers the point,
   *  a synchronous probe of the in-memory tile cache short-circuits to a
   *  single publish; otherwise we publish without elevation, kick off the
   *  async lookup, and re-publish on resolve. The async lookup is still
   *  fired on cache miss even though no caller is awaiting it — that fills
   *  the LRU so the next frame (or the next pan over the same terrain)
   *  hits the sync path. A monotonic `cursorMoveSeq` guards the resolve
   *  publish so out-of-order resolves from older frames cannot overwrite a
   *  newer value. */
  private moveCursorTo(point: number[], forceElevation: boolean): void {
    this.isCentered = false;
    this.mapSettings.isCentered = false;
    this.mapSettings.center = point;

    const geo = this.geolocation.getGeoInfo();
    const aircraftPos = [geo.longitude, geo.latitude];
    const info = computeCursorInfo(point, aircraftPos, this.mapSettings.isCentered);

    if (!forceElevation || !this.terrainUrl) {
      this.events.publish("mapCenter", { ...info, elevation: null } as MapCenterEvent);
      return;
    }

    const cached = pmtilesElevAtSync(point[0], point[1]);
    if (cached !== null) {
      this.events.publish("mapCenter", { ...info, elevation: cached } as MapCenterEvent);
      return;
    }

    this.events.publish("mapCenter", { ...info, elevation: null } as MapCenterEvent);

    const seq = ++this.cursorMoveSeq;
    getElevationOfGeoPos(point, this.terrainUrl, classCom)
      .then((elevation) => {
        if (seq !== this.cursorMoveSeq) return;
        this.events.publish("mapCenter", { ...info, elevation } as MapCenterEvent);
      })
      .catch(() => {});
  }

  private updateTrack(): void {
    if (this.trackingState !== 1) return;
    const t = this.tracking.getCurrentTrack();
    if (!t || !t.waypoints || t.waypoints.length < 2) {
      this.setSourceData("track", emptyFC());
      return;
    }
    this.setSourceData("track", lineFC(t.waypoints.map((w: number[]) => [w[0], w[1]])));
  }

  /** Projects the aircraft position to screen pixels and publishes, or null
   *  when the map is gone or the GPS fix is invalid. */
  private emitAircraftScreenPos(): void {
    if (!this.map || !this.mapReady) {
      this._aircraftScreenPos$.next(null);
      return;
    }
    const geo = this.geolocation.getGeoInfo();
    if (!geo?.valid) {
      this._aircraftScreenPos$.next(null);
      return;
    }
    const pos = this.map.project([geo.longitude, geo.latitude]);
    this._aircraftScreenPos$.next({ x: pos.x, y: pos.y });
  }

  private setAircraftVisible(visible: boolean): void {
    if (!this.map || !this.mapReady) return;
    const visibility = visible ? "visible" : "none";
    for (const layerId of ["aircraft-symbol", "aircraft-lines"]) {
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, "visibility", visibility);
      }
    }
  }

  private handleLongpress(): void {
    if (this.isCentered) return;
    this.explicitMapSelection = false;
    // The listeners run outside Angular's zone; the side list needs it.
    this.zone.run(() => {
      const zoom = this.map ? this.map.getZoom() : 10;
      this.mapObjCtrl
        .getMapObjects(this.mapSettings.center, zoom, [this.mapObjCtrl.airspace])
        .then((data) => {
          this.events.publish("mapClickItems", this.getZoom() > this.mapObjCtrl.cursorSelectionMinZoom ? data : []);
        })
        .catch((err) => console.error("longpress getMapObjects: ", err));
    });
  }

  private cancelLongpress(): void {
    if (this.longpressTimer) {
      clearTimeout(this.longpressTimer);
      this.longpressTimer = null;
    }
    this.longpressStartPos = null;
  }

  // --------------- geolocation ---------------

  private startGeolocationSubscription(): void {
    if (this.pendingTraffic) {
      this.setSourceData("traffic", this.pendingTraffic);
      this.pendingTraffic = null;
    }
    // Hide the aircraft icon while the GPS signal is unreliable. The
    // smoother stops feeding fresh positions then, so the last fix would
    // otherwise stay frozen on the map with no indication it is stale.
    this.signalStatusSubscription = this.geolocation.geoLocSmoother.signalStatus.subscribe((status) => {
      this.setAircraftVisible(status.signalReliable);
    });

    this.geolocationSubscription = this.geolocation.geolocation$.subscribe((geoInfo) => {
      if (!this.map || !this.mapReady) return;

      if (this.mapSettings.isCentered) {
        // Re-center map on aircraft
        this.map.setCenter([geoInfo.longitude, geoInfo.latitude]);
        this._aircraftScreenPos$.next(null);
        const centeredEvent: MapCenterEvent = {
          isCentered: true,
          center: [geoInfo.longitude, geoInfo.latitude],
          time: 0,
          distanceRaw: 0,
          elevation: 0,
          heading: 0,
          info: null,
        };
        this.events.publish("mapCenter", centeredEvent);
      } else {
        // Re-project so the SVG line endpoint tracks fresh GPS fixes even
        // when the map view itself hasn't moved.
        const aircraftPos = [geoInfo.longitude, geoInfo.latitude];
        const info = computeCursorInfo(this.mapSettings.center, aircraftPos, this.mapSettings.isCentered);
        this.events.publish("mapCenter", { ...info, elevation: null } as MapCenterEvent);
        this.emitAircraftScreenPos();
      }

      // Update route-active origin to follow aircraft
      this.updateRouteActiveOrigin(geoInfo);

      // Update aircraft navigation display
      if (this.aircraftImageLoaded) {
        this.setSourceData("aircraft", buildAircraftNavFC(geoInfo));
      }

      // Update track line
      this.updateTrack();
    });
  }

  private updateRouteActiveOrigin(geoInfo: { longitude: number; latitude: number }): void {
    if (!this.activeRouteCoords || this.activeRouteCoords.length === 0) return;
    this.activeRouteCoords[0] = [geoInfo.longitude, geoInfo.latitude];
    this.setSourceData("route-active", lineFC(this.activeRouteCoords));
  }

  // --------------- cleanup ---------------

  private cleanupSubscriptionsAndTimers(): void {
    if (this.trafficManager) {
      this.trafficManager.stop();
    }

    if (this.geolocationSubscription) {
      this.geolocationSubscription.unsubscribe();
      this.geolocationSubscription = null;
    }

    if (this.signalStatusSubscription) {
      this.signalStatusSubscription.unsubscribe();
      this.signalStatusSubscription = null;
    }

    if (this.vectorRefreshTimer) {
      clearTimeout(this.vectorRefreshTimer);
      this.vectorRefreshTimer = null;
    }
    this.vectorRefreshQueued = false;

    if (this.moveEndTimeout) {
      clearTimeout(this.moveEndTimeout);
      this.moveEndTimeout = null;
    }

    if (this.mapClickItemsTimeout) {
      clearTimeout(this.mapClickItemsTimeout);
      this.mapClickItemsTimeout = null;
    }

    this.cancelLongpress();
  }

  destroy(): void {
    this.cleanupSubscriptionsAndTimers();
    this.approachSectors?.remove();
    this.approachSectors = null;
    this.procedurePlanes?.remove();
    this.procedurePlanes = null;
    this.routePlane?.remove();
    this.routePlane = null;
    this.vectorPlane?.remove();
    this.vectorPlane = null;

    this._aircraftScreenPos$.complete();

    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    resetHillshadeState();
    this.ceilingOverlay.reset();
    this.terrainOverlay.reset();
    resetDemSourceState();
    resetTerrain3dState();
    this.terrain3dEnabled = false;
    // Invalidate any in-flight `refreshObstacles3d` against the now-dead map
    // and drop references to the destroyed map's WebGL layer + filter snapshot
    // so a fresh map instance starts clean.
    this.obstacle3dRefreshSeq++;
    this.obstacle3dLayer = null;
    this.vmFilterOriginals = null;

    this.mapContainer = null;

    for (const resolve of this.mapReadyResolvers) {
      resolve();
    }
    this.mapReadyResolvers = [];

    this.mapReady = false;
    this.mapInitializing = false;
  }

  deactivate(): void {
    this.cleanupSubscriptionsAndTimers();
  }

  /**
   * Stops the per-fix map work (centering, aircraft, route and track updates) while the map page
   * is not shown. Without this, every GPS fix kept updating the map sources of the hidden page.
   * Traffic keeps running: it also reports the own position. setTarget() restarts the work.
   */
  pauseGeolocation(): void {
    this.geolocationSubscription?.unsubscribe();
    this.geolocationSubscription = null;
    this.signalStatusSubscription?.unsubscribe();
    this.signalStatusSubscription = null;
  }

  /** Restarts what pauseGeolocation() stopped, if the map is on screen. */
  resumeGeolocation(): void {
    if (!this.map || this.geolocationSubscription) return;
    if (!this.mapContainer?.checkVisibility()) return;
    this.startGeolocationSubscription();
  }

  // --------------- interface methods ---------------

  mapIsReady(): Promise<any> {
    if (this.mapReady) return Promise.resolve(true);
    return new Promise<void>((resolve) => {
      this.mapReadyResolvers.push(resolve);
    });
  }

  updateMapSize(height: number, width: number): void {
    if (this.map) {
      this.map.resize();
    }
  }

  changeZoom(zoomIn: boolean, maxres?: boolean, minres?: boolean): void {
    if (!this.map) return;
    this.explicitMapSelection = false;
    const currentZoom = this.map.getZoom();
    let targetZoom = Math.round(currentZoom) + (zoomIn ? 1 : -1);
    if (targetZoom > MAX_ZOOM || minres) {
      targetZoom = MAX_ZOOM;
    }
    if (targetZoom < MIN_ZOOM || maxres) {
      targetZoom = MIN_ZOOM;
    }
    if (this.routeTour?.isPlaying) this.routeTour.zoomTo(targetZoom);
    else this.map.setZoom(targetZoom);
  }

  setCenter(center: number[], offset?: number): void {
    if (this.map) {
      this.map.setCenter([center[0], center[1]]);
    }
  }

  // --------------- camera tilt / rotate (web on-screen controls) ---------------
  // The web SPA has no multi-finger gesture, so pitch + bearing are driven by
  // hold-to-nudge buttons. A held button drives a requestAnimationFrame loop
  // that applies a per-frame delta (rate × dt) — continuous and smooth, the
  // way a native gesture feels, instead of stepped easeTo() ticks. setPitch /
  // setBearing (not a user drag) don't flip isCentered, so the map stays
  // locked to the aircraft when centered. The loop runs OUTSIDE the Angular
  // zone so it doesn't trigger change detection every frame.
  private static readonly PITCH_RATE_DEG_S = 55;
  private static readonly BEARING_RATE_DEG_S = 80;
  private camNudgeRaf: number | null = null;
  private camNudgeKind: "pitch" | "bearing" | null = null;
  private camNudgeSign = 0;
  private camNudgeLast = 0;
  private routeTour?: RouteTour;

  async previewRoute(
    points: number[][],
    onArrival?: () => void,
    onFlightAltitude?: (altitudeM: number) => Promise<void>
  ): Promise<void> {
    this.stopRoutePreview();
    if (!this.map) return;
    this.stopCameraNudge();
    this.map.stop();
    this.isCentered = false;
    this.mapSettings.isCentered = false;
    this.publishIsCenteredFalse();
    const tour = new RouteTour(this.map);
    this.routeTour = tour;
    const requestedMap = this.map;
    const elevations = await mapWithConcurrency(routeTerrainSamples(points), 4, async (point) => {
      if (this.routeTour !== tour) return null;
      try {
        return await pmtilesElevAt(point[0], point[1]);
      } catch {
        return null;
      }
    });
    if (this.routeTour !== tour || this.map !== requestedMap) return;
    const valid = elevations.filter((height): height is number => height !== null && Number.isFinite(height));
    const referenceElevationM = (valid.length ? Math.max(...valid) : requestedMap.getCenterElevation()) + 500 * 0.3048;
    // Samples begin with every route waypoint, so this is the endpoint's own
    // terrain height, not the route maximum or the camera's flight reference.
    const destination = points[points.length - 1];
    const destinationElevationM =
      elevations[points.length - 1] ?? requestedMap.queryTerrainElevation([destination[0], destination[1]]) ?? 0;
    if (onFlightAltitude) await this.zone.run(() => onFlightAltitude(referenceElevationM));
    if (this.routeTour !== tour || this.map !== requestedMap) return;
    await this.zone.runOutsideAngular(() =>
      tour.play(points, () => this.zone.run(() => onArrival?.()), referenceElevationM, destinationElevationM)
    );
    if (this.routeTour === tour) {
      this.routeTour = undefined;
      void this.refreshVectorSource();
      if (this.terrain3dEnabled) this.refreshObstacles3d();
    }
  }

  stopRoutePreview(): void {
    const wasPlaying = this.routeTour?.isPlaying;
    this.routeTour?.stop();
    this.routeTour = undefined;
    if (wasPlaying && this.map?.style) {
      this.scheduleVectorRefresh();
      this.publishVisibleAttributions();
      const center = this.map.getCenter();
      this.moveCursorTo([center.lng, center.lat], true);
    }
  }

  /** Begin continuously tilting (`kind:'pitch'`) or rotating (`'bearing'`)
   *  while a button is held. `sign` is +1 / -1 for direction. */
  startCameraNudge(kind: "pitch" | "bearing", sign: number): void {
    if (kind === "pitch" && !environment.enableThreeD) return;
    this.stopRoutePreview();
    if (!this.map) return;
    this.camNudgeKind = kind;
    this.camNudgeSign = Math.sign(sign) || 1;
    if (this.camNudgeRaf !== null) return; // loop already running, just retarget
    this.zone.runOutsideAngular(() => {
      this.camNudgeLast = performance.now();
      const step = (t: number) => {
        if (!this.map || this.camNudgeKind === null) {
          this.camNudgeRaf = null;
          return;
        }
        // Clamp dt so a delayed first frame (or a backgrounded tab) can't jump.
        const dt = Math.min(0.05, Math.max(0, (t - this.camNudgeLast) / 1000));
        this.camNudgeLast = t;
        if (this.camNudgeKind === "pitch") {
          const next = this.map.getPitch() + this.camNudgeSign * WebglMapLibreService.PITCH_RATE_DEG_S * dt;
          this.map.setPitch(Math.min(this.map.getMaxPitch(), Math.max(this.map.getMinPitch(), next)));
        } else {
          this.map.setBearing(this.map.getBearing() + this.camNudgeSign * WebglMapLibreService.BEARING_RATE_DEG_S * dt);
        }
        this.camNudgeRaf = requestAnimationFrame(step);
      };
      this.camNudgeRaf = requestAnimationFrame(step);
    });
  }

  /** Stop the held tilt/rotate loop (pointerup / leave / cancel). */
  stopCameraNudge(): void {
    this.camNudgeKind = null;
    if (this.camNudgeRaf !== null) {
      cancelAnimationFrame(this.camNudgeRaf);
      this.camNudgeRaf = null;
    }
  }

  /** Snap back to north-up and flat (the default 2D orientation). */
  resetNorthAndPitch(): void {
    if (!this.map) return;
    this.map.easeTo({ bearing: 0, pitch: 0, duration: 300 });
  }

  pauseRenderLoop(pause: boolean): void {
    // No-op: MapLibre has no render loop pause equivalent
  }

  cleanTileSources(): void {
    // No-op for now
  }

  // --------------- Tier 1: trivial ---------------

  disableClickEvent(disable: boolean): void {
    this.clickEventDisabled = disable;
  }

  clearControls(): void {
    if (!this.mapReady) return;
    this.setSourceData("control-features", emptyFC());
  }

  updateCursor(): void {
    if (this.map) {
      this.map.resize();
    }
  }

  async getTerrainInstance(): Promise<any> {
    return undefined;
  }

  async mapTest(): Promise<void> {
    // No-op — camera animation not applicable in V2
  }

  // --------------- Tier 2: storage ---------------

  async saveMapParameters(): Promise<void> {
    if (!this.map) return;
    const center = this.map.getCenter();
    this.mapSettings.center = [center.lng, center.lat];
    this.mapSettings.zoom = this.map.getZoom();
    await this.storage.set("mapSettings", this.mapSettings);
  }

  // --------------- Tier 3: control features ---------------

  setRoute(origin: number[], dest: number[], id: string): void {
    if (!this.map) return;

    this.setSourceData(
      "control-features",
      lineFC([origin, dest], { strokeColor: "rgba(255,0,255,1)", strokeWidth: 4 })
    );

    const bounds = new LngLatBounds();
    bounds.extend([origin[0], origin[1]]);
    bounds.extend([dest[0], dest[1]]);
    this.map.fitBounds(bounds, { padding: 60, duration: 1000 });
  }

  setRoutePointList(points: number[][], id: string): void {
    if (!this.map || points.length === 0) return;

    this.setSourceData("control-features", lineFC(points, { strokeColor: "rgba(255,0,255,1)", strokeWidth: 4 }));

    const bounds = new LngLatBounds();
    for (const p of points) {
      bounds.extend([p[0], p[1]]);
    }
    this.map.fitBounds(bounds, { padding: 60, duration: 1000 });
  }

  async openControlFile(data: ControlFileData, animate: boolean, onCenter: boolean): Promise<void> {
    if (!this.map || !data?.dataObj?.[0]?.mapSettings) return;

    // Always clear stale features before rendering new ones (mirrors V1's removeAllFeatures())
    this.setSourceData("control-features", emptyFC());

    // Render GeoJSON features onto control-features source
    if (data.dataObj[0].geoJSON?.features) {
      const sourceFeatures = data.dataObj[0].geoJSON.features;
      const features = sourceFeatures.map((f: any) => {
        // Apply yellow styling matching V1 controlStyleFunction
        const props = { ...f.properties };
        switch (f.geometry?.type) {
          case "Point":
          case "MultiPoint":
            props.fillColor = props.fillColor || "rgba(255,255,0,1)";
            props.strokeColor = props.strokeColor || "rgba(255,255,0,1)";
            break;
          case "LineString":
          case "MultiLineString":
            props.strokeColor = props.strokeColor || "rgba(255,255,0,1)";
            props.strokeWidth = props.strokeWidth || 6;
            break;
          case "Polygon":
          case "MultiPolygon":
            props.strokeColor = "rgba(0,0,0,1)";
            props.strokeWidth = 2;
            props.fillColor = "rgba(255,255,0,0.3)";
            break;
        }
        return { ...f, properties: props };
      });
      this.setSourceData("control-features", { type: "FeatureCollection", features });
    }

    const settings = data.dataObj[0].mapSettings;
    if (settings.center) {
      let zoom: number;
      if (settings.zoom != null) {
        zoom = settings.zoom;
      } else if (settings.resolution != null) {
        const EQUATORIAL_RES_Z0 = 156543.03;
        zoom = Math.log2(EQUATORIAL_RES_Z0 / settings.resolution);
      } else {
        zoom = this.map.getZoom();
      }

      if (animate) {
        this.mapSettings.center = settings.center;
        this.map.flyTo({ center: [settings.center[0], settings.center[1]], zoom });
        this.moveCursorTo(settings.center, true);
      } else if (onCenter) {
        this.mapSettings.center = settings.center;
        this.map.jumpTo({ center: [settings.center[0], settings.center[1]], zoom });
        this.moveCursorTo(settings.center, true);
      }
    }
  }

  // --------------- Tier 4: navigation ---------------

  fitBaseArea(area: number[][]): void {
    if (!this.map) return;
    this.map.resize();
    const bounds = new LngLatBounds([area[0][0], area[0][1]], [area[1][0], area[1][1]]);
    this.mapSettings.center = bounds.getCenter().toArray();
    this.moveCursorTo(this.mapSettings.center, false);
    this.map.fitBounds(bounds, { padding: 60, bearing: 0, pitch: 0, duration: 1000 });
  }

  fitPlannedRoute(points: PointInterface[] | undefined): boolean {
    if (!this.map) return false;
    const coordinates = (points ?? [])
      .map((point) => point.point)
      .filter((point) => point?.length >= 2 && Number.isFinite(point[0]) && Number.isFinite(point[1]));
    if (!coordinates.length) return false;

    const bounds = new LngLatBounds();
    for (const point of coordinates) bounds.extend([point[0], point[1]]);

    this.clearControls();
    this.moveCursorTo(bounds.getCenter().toArray(), false);
    this.map.fitBounds(bounds, { padding: 100, maxZoom: 12.5, duration: 1000 });
    return true;
  }

  getPposCenter(): [number, number] {
    const geo = this.geolocation.getGeoInfo();
    return [geo.longitude, geo.latitude];
  }

  async setPpos(): Promise<boolean> {
    if (!this.map) return false;

    this.clearControls();

    const center = this.getPposCenter();
    this.mapSettings.isCentered = true;
    this.isCentered = true;
    this._aircraftScreenPos$.next(null);

    const requestedMap = this.map;
    const elevation = await pmtilesElevAt(center[0], center[1]).catch(() => null);
    if (this.map !== requestedMap || !this.mapSettings.isCentered) return false;
    requestedMap.setCenterClampedToGround(true);
    requestedMap.flyTo({
      center,
      elevation: elevation ?? requestedMap.queryTerrainElevation(center) ?? 0,
    });

    const centeredEvent: MapCenterEvent = {
      isCentered: true,
      center,
      time: 0,
      distanceRaw: 0,
      elevation: 0,
      heading: 0,
      info: null,
    };
    this.events.publish("mapCenter", centeredEvent);

    return true;
  }

  async setCursor(center: number[], zoom: number): Promise<any> {
    if (!this.map) return;

    this.mapSettings.center = center;
    this.map.flyTo({ center: [center[0], center[1]], zoom });
    this.moveCursorTo(center, true);
  }

  async setDest(): Promise<boolean> {
    if (!this.map) return false;

    if (this.destinationPos.valid) {
      this.map.setCenterClampedToGround(true);
      this.map.flyTo({
        elevation: this.map.queryTerrainElevation([this.destinationPos.point[0], this.destinationPos.point[1]]) ?? 0,
        center: [this.destinationPos.point[0], this.destinationPos.point[1]],
        zoom: this.destMapLayerSetting.zoom,
      });

      this.moveCursorTo(this.destinationPos.point, true);
      return true;
    }

    return false;
  }

  // --------------- Tier 5: route drawing ---------------

  drawRoute(points: PointInterface[], opsCtrlPlannedRoute: PointInterface[]): void {
    if (!this.map) return;

    // Build route-points feature collection (circles + labels for all waypoints)
    const pointFeatures: GeoJSON.Feature[] = [];

    // Planned route waypoints
    if (opsCtrlPlannedRoute !== undefined) {
      opsCtrlPlannedRoute.forEach((p) => {
        let textColor = "rgba(100,100,100,0.5)";
        const fillColor = "rgba(255,255,255,1)";
        const strokeColor = "rgba(255,255,255,1)";

        if (p.active) {
          textColor = "rgb(0,0,0)";
        }

        pointFeatures.push({
          type: "Feature",
          properties: {
            displayName: p.displayName,
            textColor,
            fillColor,
            strokeColor,
            radius: 3,
          },
          geometry: {
            type: "Point",
            coordinates: p.point,
          },
        });
      });
    }

    // Active route waypoints
    points.forEach((p) => {
      pointFeatures.push({
        type: "Feature",
        properties: {
          displayName: p.displayName,
          textColor: "rgb(0,0,0)",
          fillColor: "rgba(255,0,255,1)",
          strokeColor: "rgba(255,255,255,1)",
          radius: 4,
        },
        geometry: {
          type: "Point",
          coordinates: p.point,
        },
      });
    });

    this.setSourceData("route-points", { type: "FeatureCollection", features: pointFeatures });

    // Build route-active: LineString from aircraft position through active waypoints
    let activeCoords: number[][] = [];
    if (!this.hideNavigRoute) {
      points.forEach((p) => {
        if (p.active) {
          activeCoords.push(p.point);
        }
      });
    }

    if (activeCoords.length > 0) {
      const geo = this.geolocation.getGeoInfo();
      activeCoords.unshift([geo.longitude, geo.latitude]);
      activeCoords = deduplicateCoords(activeCoords);
      this.activeRouteCoords = activeCoords;
      this.setSourceData("route-active", lineFC(activeCoords));
    } else {
      this.activeRouteCoords = null;
      this.setSourceData("route-active", emptyFC());
    }

    // Build route-planned: LineString through all planned waypoints
    if (opsCtrlPlannedRoute !== undefined && opsCtrlPlannedRoute.length > 0) {
      const plannedCoords = deduplicateCoords(opsCtrlPlannedRoute.map((p) => p.point));
      this.plannedRouteCoords = plannedCoords;
      this.setSourceData("route-planned", lineFC(plannedCoords));
    } else {
      this.setSourceData("route-planned", emptyFC());
      this.plannedRouteCoords = [];
    }
  }

  // --------------- Tier 6: traffic ---------------

  receiveTraffic(): void {
    console.log("=ov= traffic");

    if (!this.trafficManager) {
      this.trafficManager = new TrafficManager({
        authService: this.authService,
        mapLayerAsGeoJsonGQL: this.mapLayerAsGeoJsonGQL,
        mapLayerUpdatesGQL: this.mapLayerUpdatesGQL,
        onTrafficUpdate: (data) => {
          this._currentTrafficSubject$.next(data);

          console.log("=ov= traffic recived");
        },
        onFeaturesRendered: (fc) => {
          if (this.geolocationSubscription) this.setSourceData("traffic", fc);
          else this.pendingTraffic = fc;

          console.log("=ov= traffic recived rendered");
        },
      });
    }
    this.trafficManager.start();
  }

  // --------------- remaining interface methods ---------------

  public explicitMapSelection = false;

  public preserveExplicitMapSelection(): void {
    this.explicitMapSelection = true;
    this.mapSettings.isCentered = false;
    this.isCentered = false;
    this.publishIsCenteredFalse();
    if (this.mapClickItemsTimeout) {
      clearTimeout(this.mapClickItemsTimeout);
      this.mapClickItemsTimeout = null;
    }
  }

  async showOnMap(data: any, animate: boolean, onCenter: boolean): Promise<void> {
    if (onCenter) this.preserveExplicitMapSelection();
    await this.openControlFile(data, animate, onCenter);
  }

  getZoom(): number {
    return this.map ? this.map.getZoom() : 0;
  }

  /** Distinct CC-BY credits for the raster layers currently painting in view. */
  getVisibleAttributions(): string[] {
    if (!this.map) return [];
    const c = this.map.getCenter();
    return attributionsInView(this.attributionLayers, [c.lng, c.lat], this.map.getZoom());
  }

  private publishVisibleAttributions(): void {
    this.events.publish("mapAttributions", this.getVisibleAttributions());
  }

  // --------------- hillshade ---------------

  hillshadeAvailable(): Promise<boolean> {
    return hasHillshadePacks();
  }

  async setHillshadeEnabled(enabled: boolean): Promise<void> {
    this.hillshadeEnabled = enabled;
    await this.mapIsReady();
    if (!this.map) return;
    if (enabled) {
      await showHillshade(this.map);
    } else {
      hideHillshade(this.map);
    }
  }

  // --------------- ceiling and terrain overlays ---------------

  private readonly ceilingOverlay = new ReliefOverlay("ceiling");
  private readonly terrainOverlay = new ReliefOverlay("terrain");

  ceilingAvailable(): Promise<boolean> {
    return hasReliefPacks();
  }

  /**
   * `ceilingFt` is re-passed on every enable, because the settings page is the
   * only place it changes and the map page re-applies on every enter.
   */
  async setCeilingEnabled(enabled: boolean, ceilingFt: number): Promise<void> {
    await this.mapIsReady();
    if (!this.map) return;
    if (enabled) {
      await this.ceilingOverlay.show(this.map, ceilingColor(ceilingFt * 0.3048));
    } else {
      await this.ceilingOverlay.hide(this.map);
    }
  }

  terrainOverlayAvailable(): Promise<boolean> {
    return hasReliefPacks();
  }

  // ponytail: the GPS altitude is taken as MSL, like the DEM packs. iOS reports
  // MSL. Android's Location.getAltitude() is ellipsoidal (~48 m high in
  // Austria), so there the bands sit that much too high. Upgrade path: use
  // getMslAltitudeMeters() (API 34+) in the geolocation plugin. See TODO.md.
  async setTerrainOverlayEnabled(enabled: boolean, altitudeM: number | null): Promise<void> {
    await this.mapIsReady();
    if (!this.map) return;
    if (enabled) {
      await this.terrainOverlay.show(this.map, terrainColor(altitudeM));
    } else {
      await this.terrainOverlay.hide(this.map);
    }
  }

  /** Per position update: moves the bands without rebuilding the layers. */
  /** `null` tints nothing. */
  setTerrainOverlayAltitude(altitudeM: number | null): void {
    if (this.map) this.terrainOverlay.setColor(this.map, terrainColor(altitudeM));
  }

  // --------------- 3d terrain ---------------

  terrain3dAvailable(): Promise<boolean> {
    if (!environment.enableThreeD) return Promise.resolve(false);
    return has3dTerrainPacks();
  }

  private routePlane: RoutePlane | null = null;
  private routePointElevations = new Map<string, number | null>();
  private routePointRetryAt = new Map<string, number>();
  private routePointAltitude = (point: number[]): number => {
    if (!this.map) return NaN;
    const requestedMap = this.map;
    const key = point
      .slice(0, 2)
      .map((value) => value.toFixed(5))
      .join(",");
    if (
      !this.routePointElevations.has(key) ||
      (this.routePointElevations.get(key) === null && Date.now() >= (this.routePointRetryAt.get(key) ?? Infinity))
    ) {
      if (this.routePointElevations.size >= 256) {
        this.routePointElevations.clear();
        this.routePointRetryAt.clear();
      }
      this.routePointElevations.set(key, null);
      this.routePointRetryAt.set(key, Infinity);
      void pmtilesElevAt(point[0], point[1])
        .then((elevation) => {
          if (elevation !== null && Number.isFinite(elevation)) {
            this.routePointElevations.set(key, elevation);
            requestedMap.triggerRepaint();
          }
        })
        .catch(() => undefined)
        .finally(() => {
          this.routePointRetryAt.set(key, Date.now() + 5000);
          if (this.routePointElevations.get(key) === null) {
            setTimeout(() => {
              if (this.map === requestedMap && this.routePlane) requestedMap.triggerRepaint();
            }, 5000);
          }
        });
    }
    // Each vertex and its marker share the same decoded ground elevation.
    // Missing DEM is unknown, never sea level or the camera/chart altitude.
    return this.routePointElevations.get(key) ?? NaN;
  };
  private vectorPlane: VectorPlane | null = null;
  private procedurePlanes: ProcedurePlanes | null = null;
  private approachSectors: ApproachSectors | null = null;
  private fullVectorData: GeoJSON.FeatureCollection = emptyFC();

  private publishApproachData = (fc: GeoJSON.FeatureCollection): void => {
    if (!this.map?.style) return;
    fc = this.procedurePlanes?.setData(fc) ?? fc;
    if (this.vectorPlane?.setData("vectormap", fc)) return;
    (this.map.getSource("vectormap") as GeoJSONSource)?.setData(fc);
  };

  async setTerrain3dEnabled(enabled: boolean): Promise<void> {
    enabled = enabled && environment.enableThreeD;
    this.terrain3dEnabled = enabled;
    // Invalidate any in-flight refresh, regardless of direction. Pool workers
    // bail on stale seq, and the post-await guard re-checks map+layer state
    // so a slow refresh can't mutate the buffer after disable.
    this.obstacle3dRefreshSeq++;
    await this.mapIsReady();
    if (!this.map) return;
    // The label data moves between the 2D source and the 3D plane: build it again.
    this.labelCamera = null;
    if (enabled) {
      await show3dTerrain(this.map);
      this.installObstacle3dLayer();
      showAeroPlane(this.map);
      if (this.map.getTerrain() && !this.routePlane)
        this.routePlane = new RoutePlane(this.map, this.routePointAltitude);
      if (this.map.getTerrain() && !this.vectorPlane) this.vectorPlane = new VectorPlane(this.map);
      if (this.map.getTerrain() && !this.procedurePlanes) this.procedurePlanes = new ProcedurePlanes(this.map);
      if (this.map.getTerrain() && !this.approachSectors) {
        this.approachSectors = new ApproachSectors(this.map, this.publishApproachData);
        void this.approachSectors.setData(this.fullVectorData);
      }
      this.refreshObstacles3d();
    } else {
      this.approachSectors?.remove();
      this.approachSectors = null;
      this.procedurePlanes?.remove();
      this.procedurePlanes = null;
      this.routePlane?.remove();
      this.routePlane = null;
      this.vectorPlane?.remove();
      this.vectorPlane = null;
      (this.map.getSource("vectormap") as GeoJSONSource)?.setData(this.fullVectorData);
      hideAeroPlane(this.map);
      hide3dTerrain(this.map);
      this.removeObstacle3dLayer();
    }
  }

  // --------------- 3d obstacle layer ---------------

  // vm-* layers whose obstacle (type=6) features we may hide while the 3D
  // obstacle layer is active. Suppression is per-id and applied only AFTER
  // refreshObstacles3d successfully turns the obstacle into 3D geometry; an
  // obstacle that fails DEM sampling stays visible in 2D as a safety
  // fallback (round-3 review C1: under partial DEM coverage the previous
  // blanket `_objType !== 6` filter made obstacles disappear entirely).
  // Webcams (type 4) are hidden in all of them while 3D is on.
  private static readonly OBSTACLE_VM_TARGETS = [
    "vm-polygon-fill",
    "vm-webcam-fill",
    "vm-line",
    "vm-line-dashed",
    "vm-point-circle",
    "vm-obstacle-symbols",
    "vm-labels",
  ];

  private installObstacle3dLayer(): void {
    if (!this.map || this.obstacle3dLayer) return;
    // Pass TERRAIN_EXAGGERATION so vertex z gets scaled per-frame inside the
    // shader (not baked into the buffer at upload time). If exaggeration
    // ever becomes a runtime UI control, `setTerrainExaggeration` re-binds
    // without rebuilding obstacle geometry.
    const layer = new Obstacle3dLayer(this.obstacle3dLayerId, TERRAIN_EXAGGERATION);
    layer.setObstacleMinZoom(OBSTACLE_MIN_ZOOM);
    try {
      this.map.addLayer(layer);
      this.map.setLayerZoomRange(layer.id, this.map.getLayer("vm-runway-labels")?.minzoom ?? 0, 24);
    } catch (e) {
      console.warn("[obstacle-3d] addLayer failed; leaving 3D obstacle layer uninstalled", e);
      return;
    }
    this.obstacle3dLayer = layer;
    // Install with an empty rendered-id set so vm-* obstacles remain
    // visible until the first refresh tells us which ones now exist in 3D.
    this.applyVmObstacleSuppression([]);
  }

  private removeObstacle3dLayer(): void {
    this.obstacleCoverage = undefined;
    if (this.map && this.obstacle3dLayer && this.map.getLayer(this.obstacle3dLayerId)) {
      this.map.removeLayer(this.obstacle3dLayerId);
    }
    this.obstacle3dLayer = null;
    this.restoreVmFilters();
  }

  // Compose each vm-* layer's original filter with a suppression clause that
  // hides two sets. Obstacles are hidden per id: only features that match both
  // `_objType == 6` AND `id IN renderedIds`. Combining with `_objType == 6`
  // keeps that part scoped to obstacles even though the MAP_PROCESSES fan-out
  // gives per-DB id spaces (id=42 in ofm_navdata vs id=42 in weather are
  // different rows — only the type=6 ones share the obstacle behaviour).
  // Webcams (`_objType == 4`) are all hidden, whatever their id: no webcam
  // cone is drawn in 3D.
  //
  // The first call snapshots the layers' baseline filters into
  // `vmFilterOriginals`; subsequent calls reuse the snapshot so we never
  // compose-over-composed. Snapshot is restored on layer removal. vm-*
  // filters are still assumed immutable while 3D is on — no other code
  // path mutates them today.
  private applyVmObstacleSuppression(obstacleIds: Iterable<number>): void {
    if (!this.map) return;
    if (!this.vmFilterOriginals) {
      const originals: Record<string, unknown> = {};
      for (const id of WebglMapLibreService.OBSTACLE_VM_TARGETS) {
        const layer = this.map.getLayer(id) as { filter?: unknown } | undefined;
        if (!layer) continue;
        originals[id] = layer.filter ?? null;
      }
      this.vmFilterOriginals = originals;
    }
    const obs = Array.from(obstacleIds);
    // Hide a 2D feature iff it was promoted to 3D (type 6 obstacle in the
    // obstacle id set), and every webcam (type 4): a flat cone cuts into the
    // terrain in 3D, so webcams are left out of 3D. Scoping by `_objType` keeps
    // the per-id match correct despite the MAP_PROCESSES fan-out giving per-DB
    // id spaces. `["id"]` reads `feature.id` (top-level,
    // set in refreshVectorSource as `f.id = dataRaw[i].id`); `["get","id"]`
    // would read `feature.properties.id`, which is undefined here.
    const exclude: any = [
      "!",
      [
        "any",
        ["all", ["==", ["get", "_objType"], 6], ["in", ["id"], ["literal", obs]]],
        ["==", ["get", "_objType"], 4],
      ],
    ];
    for (const [id, origFilter] of Object.entries(this.vmFilterOriginals)) {
      if (!this.map.getLayer(id)) continue;
      const composed = origFilter ? ["all", origFilter, exclude] : exclude;
      this.map.setFilter(id, composed as any);
    }
  }

  private restoreVmFilters(): void {
    if (!this.map || !this.vmFilterOriginals) {
      this.vmFilterOriginals = null;
      return;
    }
    for (const [id, filter] of Object.entries(this.vmFilterOriginals)) {
      if (this.map.getLayer(id)) this.map.setFilter(id, (filter as any) ?? null);
    }
    this.vmFilterOriginals = null;
  }

  /**
   * Fetch type=6 (obstacle) geometries in the visible bbox, DEM-sample every
   * vertex with bounded concurrency, and rebuild the 3D layer's vertex
   * buffers. Per-feature: if ANY vertex lacks DEM coverage the feature is
   * skipped in 3D and left visible in 2D (round-3 C1 — DEM-missing obstacles
   * must remain on the map). After the buffers are uploaded we install a
   * per-id vm-* suppression filter for the ids that actually rendered.
   *
   * Concurrent calls are sequenced via `obstacle3dRefreshSeq` so a stale
   * slower refresh can't overwrite a newer one. Per-worker rejections are
   * caught (round-3 C8 — `Promise.all` propagation would tank the whole
   * refresh into an unhandled rejection on a single transient miss).
   */
  private async refreshObstacles3d(): Promise<void> {
    // A call during a refresh is queued, not dropped: it carries the newer view.
    if (this.obstacleRefreshBusy) {
      this.obstacleRefreshQueued = true;
      return;
    }
    this.obstacleRefreshBusy = true;
    try {
      do {
        this.obstacleRefreshQueued = false;
        await this.loadObstacles3d();
      } while (this.obstacleRefreshQueued);
    } finally {
      this.obstacleRefreshBusy = false;
    }
  }

  private async loadObstacles3d(): Promise<void> {
    if (!this.map || !this.terrain3dEnabled) return;
    const requestedMap = this.map;
    const visible = this.map.getBounds();
    const view = {
      west: visible.getWest(),
      south: visible.getSouth(),
      east: visible.getEast(),
      north: visible.getNorth(),
    };
    const cached = this.obstacleCoverage;
    if (cached?.map === this.map && performance.now() - cached.at < 30000 && containsView(cached.bounds, view)) return;
    const seq = ++this.obstacle3dRefreshSeq;
    // Abort signal threaded into pmtilesElevAt so an in-flight fetch/decode
    // bails immediately when the seq is invalidated (round-3 C4).
    const abortCtrl = new AbortController();
    const onStaleSeq = () => abortCtrl.abort();

    const bounds = expandedView(view);
    const minX = safeNum(bounds.west);
    const minY = safeNum(bounds.south);
    const maxX = safeNum(bounds.east);
    const maxY = safeNum(bounds.north);

    let rows: any[];
    try {
      rows = await this.database.namedQuery("mapGeometriesInBoundsByType", {
        minX,
        maxX,
        minY,
        maxY,
        // 6 = obstacles (towers/cables). Webcams (4) are hidden in 3D.
        types: [6],
      });
    } catch (e) {
      console.warn("[obstacle-3d] query failed", e);
      return;
    }
    if (seq !== this.obstacle3dRefreshSeq) {
      onStaleSeq();
      return;
    }

    const OBSTACLE_OFFSET_M = 25;
    // Per cable segment, sample this many terrain points so the curtain bottom
    // HUGS the ground (drops into valleys) instead of following the straight
    // chord between tower bases. The cable top stays a straight line between
    // endpoints; only the curtain's lower edge is densified. Each subdivision
    // costs one DEM lookup per vertex, so this directly multiplies the
    // elevation-fetch load on every moveend — keep it modest.
    const CURTAIN_SUBDIVISIONS = 4;
    const scaleElev = (e: number) => e + OBSTACLE_OFFSET_M;

    // Flatten every vertex that needs a DEM lookup into a single list, run the
    // lookups with one shared concurrency cap, then re-group per feature.
    //   • Point / MultiPoint (type 6 towers): sample each vertex.
    //   • LineString (type 6 cables): sample a DENSE polyline (K subdivisions
    //     per segment) so the curtain bottom follows the terrain underneath.
    type VertexJob = { coord: [number, number] };
    type GeomKind = "Point" | "MultiPoint" | "LineString";
    interface ParsedRow {
      id: number;
      type: number;
      kind: GeomKind;
      coords: [number, number][]; // Point/MultiPoint: vertices; LineString: DENSE polyline
      sampleStart: number;
      sampleCount: number;
      origCount?: number; // LineString: original (pre-densify) vertex count
      subdiv?: number; // LineString: K subdivisions per original segment
      lighted?: boolean; // Point / MultiPoint: lighted obstacle icon
      strokeColor?: unknown; // LineString: cable colour, the same as in 2D
    }
    const parsed: ParsedRow[] = [];
    const vertexJobs: VertexJob[] = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      let feat: any;
      try {
        feat = JSON.parse(r.geoJson);
      } catch {
        continue;
      }
      const geom = feat?.geometry;
      if (!geom) continue;

      if (geom.type === "LineString") {
        const V = geom.coordinates as [number, number][];
        if (!Array.isArray(V) || V.length < 2) continue;
        // Densify: (n-1)*K + 1 points. dense[s*K] === original vertex V[s].
        const K = CURTAIN_SUBDIVISIONS;
        const dense: [number, number][] = [];
        for (let s = 0; s < V.length - 1; s++) {
          const [ax, ay] = V[s];
          const [bx, by] = V[s + 1];
          for (let k = 0; k < K; k++) {
            const f = k / K;
            dense.push([ax + (bx - ax) * f, ay + (by - ay) * f]);
          }
        }
        dense.push(V[V.length - 1]);
        const start = vertexJobs.length;
        for (const c of dense) vertexJobs.push({ coord: c });
        parsed.push({
          id: r.id,
          type: 6,
          kind: "LineString",
          coords: dense,
          sampleStart: start,
          sampleCount: dense.length,
          origCount: V.length,
          subdiv: K,
          strokeColor: feat?.properties?.strokeColor,
        });
        continue;
      }

      let coords: [number, number][] | null = null;
      if (geom.type === "Point") {
        coords = [geom.coordinates as [number, number]];
      } else if (geom.type === "MultiPoint") {
        if (Array.isArray(geom.coordinates) && geom.coordinates.length >= 1) {
          coords = geom.coordinates as [number, number][];
        }
      }
      if (!coords) continue;
      const start = vertexJobs.length;
      for (const c of coords) vertexJobs.push({ coord: c });
      parsed.push({
        id: r.id,
        type: 6,
        kind: geom.type,
        coords,
        sampleStart: start,
        sampleCount: coords.length,
        lighted: isLightedObstacleSrc(feat?.properties?.src),
      });
    }

    const DEM_CONCURRENCY = 16;
    // Worker swallows per-job errors and stale-seq aborts; either way the
    // result is null which the regroup loop treats as "DEM missing → keep
    // 2D fallback". A throw here would surface as an unhandled rejection
    // because setTerrain3dEnabled calls refreshObstacles3d fire-and-forget.
    const elevs = await mapWithConcurrency(vertexJobs, DEM_CONCURRENCY, async (job) => {
      if (seq !== this.obstacle3dRefreshSeq) return null;
      try {
        return await pmtilesElevAt(job.coord[0], job.coord[1], abortCtrl.signal);
      } catch {
        return null;
      }
    });

    // Re-validate against ALL preconditions before mutating map state. The
    // long await above gives 3D-disable, map.destroy, and re-enable plenty
    // of time to fire — without this guard a stale refresh would overwrite
    // a current one.
    if (seq !== this.obstacle3dRefreshSeq) {
      onStaleSeq();
      return;
    }
    if (!this.map || !this.terrain3dEnabled || !this.obstacle3dLayer) return;

    const lineCoords: number[] = [];
    const lineColors: number[] = [];
    const lineGroundCoords: number[] = [];
    const pointCoords: number[] = [];
    const renderedIds: number[] = []; // type 6 obstacles
    let dropped = 0;
    for (const row of parsed) {
      const slice = elevs.slice(row.sampleStart, row.sampleStart + row.sampleCount);
      if (slice.some((e) => e == null)) {
        dropped++;
        continue;
      }

      if (row.kind === "LineString") {
        // `coords` is the DENSE polyline; `slice[d]` is the bare terrain at
        // dense point d. The CABLE TOP stays a straight line between towers, so
        // top z at dense point d is linearly interpolated from the two bounding
        // ORIGINAL vertices' (terrain+offset) heights — NOT the terrain under d.
        // The CURTAIN BOTTOM follows the terrain (`slice[d]`), so the sheet
        // drops into valleys and meets the ground all along the span.
        const K = row.subdiv as number;
        const orig = row.origCount as number;
        const topElevAt = (d: number): number => {
          const s = Math.min(Math.floor(d / K), orig - 2); // bounding segment
          const f = (d - s * K) / K;
          const za = (slice[s * K] as number) + OBSTACLE_OFFSET_M;
          const zb = (slice[(s + 1) * K] as number) + OBSTACLE_OFFSET_M;
          return za + (zb - za) * f;
        };
        const top = row.coords.map(([lng, lat], d) => lngLatElevToMercator(lng, lat, topElevAt(d)));
        const ground = row.coords.map(([lng, lat], d) => lngLatElevToMercator(lng, lat, slice[d] as number));
        // Same default as the 2D obstacle line layer.
        const rgba = cssColorToRgba(row.strokeColor ?? "rgb(0,0,0)");
        for (let d = 0; d < top.length - 1; d++) {
          lineColors.push(...rgba);
          lineCoords.push(...top[d], ...top[d + 1]);
          lineGroundCoords.push(...ground[d], ...ground[d + 1]);
        }
        renderedIds.push(row.id);
        continue;
      }

      // Point / MultiPoint towers.
      const merc = row.coords.map(([lng, lat], i) => lngLatElevToMercator(lng, lat, scaleElev(slice[i] as number)));
      for (const [mx, my, mz] of merc) pointCoords.push(mx, my, mz, row.lighted ? 1 : 0);
      renderedIds.push(row.id);
    }

    this.obstacle3dLayer.setData(
      new Float64Array(lineCoords),
      new Float64Array(pointCoords),
      new Float64Array(lineGroundCoords),
      new Float64Array(0),
      new Float32Array(lineColors)
    );
    // Hide the 2D copies of the features we just promoted to 3D. Dropped
    // features (no DEM) stay visible via the vm-* layers (safety fallback).
    this.applyVmObstacleSuppression(renderedIds);
    // Missing DEM samples must be retried; never cache an incomplete build.
    if (dropped === 0) this.obstacleCoverage = { map: requestedMap, bounds, at: performance.now() };
    console.log(
      `[obstacle-3d] obstacles=${renderedIds.length} dropped=${dropped} lineVerts=${lineCoords.length / 3} pointVerts=${pointCoords.length / 4}`
    );
  }

  ngOnDestroy(): void {
    this.destroy();
  }
}
